import { getDriver, REDUCED } from "@theme/1scp-scroll";

/**
 * - `data-scpd-lines`: a heading rises line by line.
 * - `data-scpd-rise`: the element rises and fades in.
 * - `data-scpd-fade`: fade only, for elements already placed with a transform.
 * - `data-scpd-stagger`: the children rise one after another.
 * - `data-scpd-unveil`: a picture frame opens from the bottom up while its
 *   picture settles from a slight zoom.
 * - `data-scpd-count`: a figure counts up from zero to the number it was
 *   printed with (see `setUpCount`).
 * - `data-scpd-delay="150"`: waits that many ms first.
 * - `data-scpd-cover` on a section: it slides over the section before it,
 *   which trails behind at a third of the scroll (not on a phone).
 */

document.documentElement.classList.add("scpd-motion");

const REVEAL_TIME = 1250;
// The picture's zoom outlasts the frame's opening (see `--scpd-unveil-time` in global.css).
const UNVEIL_TIME = 1800;
const STEP = 90;
// No cover trail on a phone: the section behind would drift under the cover.
const PHONE = matchMedia("(max-width: 767px)");

/** Marking it settled hands the element's transform back to its own styles. */
const reveal = (el) => {
  if (el.classList.contains("is-in")) return;
  el.classList.add("is-in");
  seen.unobserve(el);

  const delay = Number.parseInt(el.dataset.scpdDelay, 10) || 0;
  const steps = el.hasAttribute("data-scpd-stagger") ? el.children.length - 1 : 0;
  const time = el.hasAttribute("data-scpd-unveil") ? UNVEIL_TIME : REVEAL_TIME;
  setTimeout(() => el.classList.add("is-settled"), delay + time + Math.max(0, steps) * STEP + 100);
};

const seen = new IntersectionObserver(
  (entries) => entries.forEach((entry) => entry.isIntersecting && reveal(entry.target)),
  { rootMargin: "0px 0px -12% 0px" }
);

// Something in the last 12% of the page never crosses that line, so being wholly on screen counts too.
const whole = new IntersectionObserver(
  (entries) =>
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      whole.unobserve(entry.target);
      if (figures.has(entry.target)) count(entry.target);
      else reveal(entry.target);
    }),
  { threshold: 1 }
);

const watch = (el) => {
  if (el.classList.contains("is-in")) return;
  if (REDUCED.matches) {
    el.classList.add("is-in", "is-settled");
    return;
  }
  seen.observe(el);
  whole.observe(el);
  // Anything on the first screen arrives at once, even below the observer's margin.
  requestAnimationFrame(() => {
    const box = el.getBoundingClientRect();
    if (box.bottom > 0 && box.top < innerHeight) reveal(el);
  });
};

const delayOf = (el) => {
  const ms = Number.parseInt(el.dataset.scpdDelay, 10);
  if (ms > 0) el.style.setProperty("--scpd-delay", `${ms}ms`);
};

const sources = new WeakMap();
const widths = new WeakMap();

const split = (el) => {
  if (!sources.has(el)) sources.set(el, el.innerHTML);
  el.innerHTML = sources.get(el);

  const units = [];
  for (const node of [...el.childNodes]) {
    if (node.nodeType === Node.TEXT_NODE) {
      for (const part of node.textContent.split(/(\s+)/)) {
        if (!part) continue;
        if (/^\s+$/.test(part)) {
          units.push(document.createTextNode(" "));
        } else {
          const word = document.createElement("span");
          word.className = "scpd-word";
          word.textContent = part;
          units.push(word);
        }
      }
    } else {
      units.push(node);
    }
  }
  el.replaceChildren(...units);

  const lines = [];
  let current = [];
  let top = null;
  for (const unit of units) {
    if (unit.nodeType === Node.ELEMENT_NODE && unit.tagName === "BR") {
      lines.push(current);
      current = [];
      top = null;
      continue;
    }
    if (unit.nodeType === Node.ELEMENT_NODE) {
      const y = Math.round(unit.getBoundingClientRect().top);
      if (top !== null && Math.abs(y - top) > 2) {
        lines.push(current);
        current = [];
      }
      top = y;
    }
    current.push(unit);
  }
  if (current.length) lines.push(current);

  const built = lines
    .map((line) => {
      while (line.length && line[0].nodeType === Node.TEXT_NODE) line.shift();
      while (line.length && line.at(-1).nodeType === Node.TEXT_NODE) line.pop();
      return line;
    })
    .filter((line) => line.length)
    .map((line, index, all) => {
      const mask = document.createElement("span");
      mask.className = "scpd-line";
      const inner = document.createElement("span");
      inner.className = "scpd-line__inner";
      inner.style.setProperty("--scpd-line", String(index));
      for (const unit of line) {
        inner.append(unit.nodeType === Node.ELEMENT_NODE && unit.classList.contains("scpd-word") ? unit.textContent : unit);
      }
      // Keep the break's space so screen readers and copies don't join the words.
      if (index < all.length - 1) inner.append(" ");
      mask.append(inner);
      return mask;
    });

  el.replaceChildren(...built);
  el.style.setProperty("--scpd-lines", String(built.length));
  el.classList.add("is-split");
  widths.set(el, el.getBoundingClientRect().width);
};

const setUpLines = (el) => {
  if (el.classList.contains("is-split")) return;
  delayOf(el);
  split(el);
  watch(el);
};

const setUpRise = (el) => {
  delayOf(el);
  watch(el);
};

const setUpStagger = (el) => {
  delayOf(el);
  [...el.children].forEach((child, index) => child.style.setProperty("--scpd-i", String(index)));
  watch(el);
};

/**
 * The figure is read from the element's own text, so it can be typed in the
 * editor or filled from product data:
 * - the number is the first run of digits, kept with the separator it was
 *   typed with (13.000 ends on 13.000);
 * - what stands around it stays as written, so "1.000+" and "21 g" keep the
 *   "+" and the " g";
 * - text without a number, such as "Own data", is left alone.
 * It is set to zero at its finished width as soon as it is found, so the
 * full number never flashes up first and a centred figure does not shuffle
 * sideways while its digits turn. The last frame puts the text back exactly.
 */
const FIGURE = /^(\D*?)(\d{1,3}(?:[.,]\d{3})+|\d+)(.*)$/s;
const COUNT_TIME = 1600;
const countEase = (t) => 1 - Math.pow(1 - t, 4);
const counting = new WeakSet();

const withSeparator = (value, separator) =>
  separator ? String(value).replace(/\B(?=(\d{3})+(?!\d))/g, separator) : String(value);

const count = (el) => {
  const figure = figures.get(el);
  counted.unobserve(el);
  if (!figure || counting.has(el)) return;
  counting.add(el);

  const { text, before, after, separator, target, from } = figure;
  const start = performance.now();
  const tick = (now) => {
    const t = Math.min(1, (now - start) / COUNT_TIME);
    if (t < 1) {
      el.textContent = before + withSeparator(Math.round(from + (target - from) * countEase(t)), separator) + after;
      requestAnimationFrame(tick);
    } else {
      el.textContent = text;
      el.style.minWidth = "";
    }
  };
  requestAnimationFrame(tick);
};

const figures = new WeakMap();

const counted = new IntersectionObserver(
  (entries) => entries.forEach((entry) => entry.isIntersecting && count(entry.target)),
  { rootMargin: "0px 0px -12% 0px" }
);

const setUpCount = (el) => {
  if (REDUCED.matches || figures.has(el)) return;
  const text = el.textContent.trim();
  const match = text.match(FIGURE);
  if (!match) return;
  const [, before, digits, after] = match;
  // A decimal (1,5 g) is not counted: its whole part alone would run 0,5 to 1,5.
  if (/^[.,]\d/.test(after)) return;
  const target = Number(digits.replace(/[.,]/g, ""));
  if (!target) return;
  const separator = (digits.match(/[.,]/) || [""])[0];
  // A year counts from a century earlier, so it does not pass through the low hundreds.
  const from = !separator && target >= 1000 && target <= 2100 ? target - 100 : 0;
  figures.set(el, { text, before, after, separator, target, from });

  el.style.display = "inline-block";
  el.style.minWidth = `${el.getBoundingClientRect().width}px`;
  el.textContent = before + withSeparator(from, separator) + after;

  counted.observe(el);
  whole.observe(el);
  // The same rule as the entrances: a figure on the first screen starts at once.
  requestAnimationFrame(() => {
    const box = el.getBoundingClientRect();
    if (box.bottom > 0 && box.top < innerHeight) count(el);
  });
};

const setUpCover = (section) => {
  if (REDUCED.matches || section.dataset.scpdCoverReady) return;
  const behind = section.closest(".shopify-section")?.previousElementSibling;
  if (!behind) return;
  section.dataset.scpdCoverReady = "true";

  let top = 0;
  const measure = () => {
    top = section.getBoundingClientRect().top + scrollY;
  };

  let last = -1;
  // Starts once the cover has come a fifth of the way up the screen, and
  // the section behind then trails at a third of the scroll.
  const write = (y) => {
    const risen = PHONE.matches ? 0 : Math.min(innerHeight, Math.max(0, y + innerHeight * 0.8 - top));
    if (risen === last) return;
    last = risen;
    behind.style.transform = risen ? `translate3d(0, ${(risen * 0.35).toFixed(1)}px, 0)` : "";
  };

  // Only while the join is on screen; the section behind is reset once the
  // cover has gone back below the fold.
  // Reset on both sides: a transform left on the section behind would break anything fixed or sticky in it.
  const observer = new IntersectionObserver(
    ([entry]) => {
      if (entry.isIntersecting) {
        measure();
        getDriver().add(write);
      } else {
        getDriver().remove(write);
        write(-Infinity);
      }
    },
    { rootMargin: "0px 0px 10% 0px" }
  );
  observer.observe(section);
  addEventListener("resize", measure, { passive: true });

  covers.set(section, () => {
    observer.disconnect();
    removeEventListener("resize", measure);
    getDriver().remove(write);
    write(-Infinity);
  });
};

const covers = new WeakMap();

const init = (root = document) => {
  root.querySelectorAll("[data-scpd-rise], [data-scpd-fade], [data-scpd-unveil]").forEach(setUpRise);
  root.querySelectorAll("[data-scpd-stagger]").forEach(setUpStagger);
  root.querySelectorAll("[data-scpd-cover]").forEach(setUpCover);

  // Measured with the fallback font, the line breaks and a figure's held
  // width would be wrong.
  // Headings wait at most a moment for the fonts, as they are hidden until split (often the page's
  // largest text); if the fonts come later, they are split again.
  const lines = root.querySelectorAll("[data-scpd-lines]");
  if (lines.length) {
    fontsOrSoon().then(() => lines.forEach(setUpLines));
    document.fonts.ready.then(() => lines.forEach((el) => el.classList.contains("is-split") && split(el)));
  }
  const counts = root.querySelectorAll("[data-scpd-count]");
  if (counts.length) document.fonts.ready.then(() => counts.forEach(setUpCount));
};

const fontsOrSoon = () => Promise.race([document.fonts.ready, new Promise((done) => setTimeout(done, 300))]);

init();

document.addEventListener("shopify:section:load", (event) => init(event.target));
document.addEventListener("shopify:section:unload", (event) => {
  event.target.querySelectorAll("[data-scpd-cover]").forEach((section) => {
    covers.get(section)?.();
    covers.delete(section);
  });
});

// Re-split on a width change without replaying: an arrived heading stays arrived.
function resplit() {
  document.querySelectorAll("[data-scpd-lines].is-split").forEach((el) => {
    const width = el.getBoundingClientRect().width;
    if (Math.abs(width - (widths.get(el) || 0)) > 1) split(el);
  });
}

let resizing = null;
addEventListener(
  "resize",
  () => {
    clearTimeout(resizing);
    resizing = setTimeout(resplit, 200);
  },
  { passive: true }
);
